import { parseTrialStatus, type TrialStatus } from '@suvoda-uat/domain';
import { redirect } from '@tanstack/react-router';
import { queryClient, trialQueryOptions } from './queries.js';

export type LifecyclePrimaryRoute = '/upload' | '/verification' | '/uat';
export type LifecycleRoutePath =
  | LifecyclePrimaryRoute
  | `/uat/cases/${string}`
  | `/uat/cases/${string}/edit`;

export type LifecycleDestination = {
  readonly to: LifecyclePrimaryRoute;
  readonly search: { readonly trialId: string };
};

export type LifecycleGuardDecision =
  | { readonly kind: 'allow' }
  | { readonly kind: 'redirect'; readonly destination: LifecycleDestination };

const PRIMARY_DESTINATION_BY_STAGE: Record<TrialStatus, LifecyclePrimaryRoute> = {
  setting_up: '/upload',
  generating: '/verification',
  in_review: '/uat',
  finalised: '/uat',
  failed: '/verification',
};

export const lifecycleDestinationForStage = (
  rawStatus: unknown,
  trialId: string,
): LifecycleDestination => {
  const status = parseTrialStatus(rawStatus);
  return { to: PRIMARY_DESTINATION_BY_STAGE[status], search: { trialId } };
};

const isCaseRoute = (currentPath: LifecycleRoutePath): boolean =>
  currentPath.startsWith('/uat/cases/');

const isAllowedForStage = (status: TrialStatus, currentPath: LifecycleRoutePath): boolean => {
  switch (status) {
    case 'setting_up':
      return currentPath === '/upload';
    case 'generating':
      return currentPath === '/verification';
    case 'in_review':
    case 'finalised':
      return currentPath === '/uat' || isCaseRoute(currentPath);
    case 'failed':
      return currentPath === '/verification' || currentPath === '/upload';
  }
};

export const guardLifecycleRoute = (input: {
  readonly status: unknown;
  readonly trialId: string;
  readonly currentPath: LifecycleRoutePath;
}): LifecycleGuardDecision => {
  const status = parseTrialStatus(input.status);
  if (isAllowedForStage(status, input.currentPath)) return { kind: 'allow' };

  return {
    kind: 'redirect',
    destination: lifecycleDestinationForStage(status, input.trialId),
  };
};

export type LifecycleBeforeLoadInput = {
  readonly search: { readonly trialId: string };
};

const throwRedirect = (destination: LifecycleDestination): never => {
  throw redirect({ to: destination.to, search: destination.search });
};

export const createLifecycleBeforeLoad =
  (currentPath: LifecycleRoutePath) =>
  async ({ search }: LifecycleBeforeLoadInput): Promise<void> => {
    const trialId = search.trialId.trim();
    if (!trialId) throw redirect({ to: '/' });

    // Route authorization must not use stale upload cache after StartGeneration.
    const trial = await queryClient.fetchQuery(trialQueryOptions(trialId));
    const decision = guardLifecycleRoute({ status: trial.status, trialId, currentPath });
    if (decision.kind === 'redirect') throwRedirect(decision.destination);
  };
